import BaseService from './base.service';
import { GetInductionStatusSingleResponse } from '@/common/model/response/get-induction-status-single-response.model';
import AppConstants from '../constants/app.constants';
import ErrorCdMsgConstants from '../constants/error-cd-msg.constants';
import FlashMessage from '../components/flash-message.component';
import SettingService from './setting.service';
import store from '../store';
import { InductionStatus } from '@/tsort/enum/induction-status.enum';
import RouterUtils from '../utils/router.util';
import HTTPClient from '../http-client';
export default class InductionService extends BaseService {
  private GET_INDUCTION_STATUS_URL = AppConstants.GET_INDUCTION_STATUS_URL;
  private CLOSE_INDUCTION_URL = `/func/close-induction`;
  private OPEN_INDUCTION_URL = `/func/open-induction`;
  private settingService = new SettingService();

  /**
   * インダクションのステータスを取得する非同期メソッドです。
   *
   * @param inductionId インダクションID
   * @returns Promise<RcsGetInductionStatusSingleResponse>
   */
  public async getInductionStatus(inductionId: string): Promise<GetInductionStatusSingleResponse> {
    const axiosResponse = await this.HTTP.get<GetInductionStatusSingleResponse>(this.GET_INDUCTION_STATUS_URL, {
      params: { inductionId: inductionId }
    });
    const dataResponse = HTTPClient.convertAxiosResponseToResponse(axiosResponse);
    return dataResponse;
  }

  /**
   * インダクションをクローズする非同期メソッドです。
   *
   * @param inductionId インダクションID
   */
  public async closeInduction(inductionId: string): Promise<void> {
    await this.HTTP.post<void>(this.CLOSE_INDUCTION_URL, {
      inductionId: inductionId
    });
  }

  /**
   * インダクションをオープンする非同期メソッドです。
   *
   * @param inductionId インダクションID
   */
  public async openInduction(inductionId: string): Promise<void> {
    await this.HTTP.post<void>(this.OPEN_INDUCTION_URL, {
      inductionId: inductionId
    });
  }

  /**
   * インダクションのステータスを変更します。
   *
   * @param closeInductionFlag インダクションを閉じるかどうか
   * @param targetPageName ターゲットページ名
   */
  public async changeInductionStatus(closeInductionFlag: boolean, targetPageName: string) {
    const linkInductionRes = await this.settingService
      .getLinkInduction(this.getExternalDeviceId(), AppConstants.DEVICE_TYPE)
      .catch(() => {
        return null;
      });
    if (linkInductionRes && linkInductionRes.mstInduction) {
      const inductionInfo = linkInductionRes.mstInduction;
      const inductionId = inductionInfo.inductionId;
      this.updateInductionDeviceInfo(inductionId, inductionInfo.inductionName, inductionInfo.deviceId);
      const inductionStatusRes = await this.getInductionStatus(inductionId).catch(() => {
        return null;
      });
      const inductionStatus = (inductionStatusRes as GetInductionStatusSingleResponse)?.status;
      if (closeInductionFlag) {
        // インダクションを閉じる場合
        if (inductionStatusRes === null || inductionStatus === InductionStatus.START) {
          // インダクションが開始されているか、インダクションステータスを取得できない場合は、インダクションを閉じるためにAPIを呼び出します
          await this.closeInduction(inductionId);
        }
      } else {
        // インダクションを開く場合
        if (inductionStatusRes === null || inductionStatus !== InductionStatus.START) {
          // インダクションが開始されていないか、インダクションステータスを取得できない場合は、インダクションを開くためにAPIを呼び出します
          await this.openInduction(inductionId);
        }
      }
      await this.goToTargetPage(targetPageName);
    } else {
      this.updateInductionInfo('', '');
      await this.goToTargetPage(AppConstants.NAME_SETTING_PAGE);
      let errCd = ErrorCdMsgConstants.FUNC_WITHOUT_SELECTING_INDUCTION_SETTING_REQUIRED_TO_RESUME_ERR_CD;
      let errMsg = ErrorCdMsgConstants.FUNC_WITHOUT_SELECTING_INDUCTION_SETTING_REQUIRED_TO_RESUME_ERR_MSG;
      if (closeInductionFlag) {
        errCd = ErrorCdMsgConstants.FUNC_WITHOUT_SELECTING_INDUCTION_SETTING_REQUIRED_TO_TAKE_BREAK_ERR_CD;
        errMsg = ErrorCdMsgConstants.FUNC_WITHOUT_SELECTING_INDUCTION_SETTING_REQUIRED_TO_TAKE_BREAK_ERR_MSG;
      }
      FlashMessage.getInstance().showErrorMsgWithErrorCode(errCd, errMsg);
    }
  }

  /**
   * インダクションのステータスをチェックし、インダクション休止ページにリダイレクトします。
   *
   * @param inductionProcessingMode インダクション処理モード
   * @param inductionId インダクションID
   * @returns インダクションステータス
   */
  public async isRedirectToBreakInductionPage(inductionProcessingMode: boolean, inductionId: string) {
    if (inductionProcessingMode) {
      const inductionStatusRes = await this.getInductionStatus(inductionId).catch(() => {
        return null;
      });
      // インダクションのステータスが開始されていない場合、インダクション休止ページにリダイレクトする
      if (
        inductionStatusRes &&
        (inductionStatusRes as GetInductionStatusSingleResponse)?.status !== InductionStatus.START
      ) {
        store.dispatch('SettingStore/updateInductionBreakingFlag', true, { root: true });
        await RouterUtils.goToRouterName(AppConstants.NAME_BREAK_INDUCTION_PAGE);
        return true;
      }
    }
    return false;
  }

  /**
   * 外部デバイスIDを取得します。
   *
   * @returns 外部デバイスID
   */
  private getExternalDeviceId() {
    return store.getters['SettingStore/externalDeviceId'];
  }

  /**
   * インダクションの情報を更新します。
   *
   * @param inductionId インダクションID
   * @param inductionName インダクション名
   * @param deviceId デバイスID
   */
  private updateInductionDeviceInfo(inductionId: string, inductionName: string, deviceId: string) {
    this.updateInductionInfo(inductionId, inductionName);
    store.dispatch('SettingStore/updateDeviceId', deviceId, { root: true });
  }

  /**
   * インダクションを更新します。
   *
   * @param inductionId インダクションID
   * @param inductionName インダクション名
   */
  private updateInductionInfo(inductionId: string, inductionName: string) {
    store.dispatch('SettingStore/updateInductionId', inductionId, { root: true });
    store.dispatch('SettingStore/updateInductionName', inductionName, { root: true });
  }

  /**
   * ターゲットページに遷移します。
   *
   * @param targetPageName ターゲットページ名
   */
  private async goToTargetPage(targetPageName: string) {
    store.dispatch(
      'SettingStore/updateInductionBreakingFlag',
      targetPageName === AppConstants.NAME_BREAK_INDUCTION_PAGE,
      { root: true }
    );
    await RouterUtils.goToRouterName(targetPageName);
  }
}
